import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BBALoader from '../../components/BBALoader';
import { Search, Filter, Plus, Package, Star, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import BackButton from '../../components/BackButton';
import Modal from '../../components/Modal';
import { collection, addDoc, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  normalizeAlaCarteItem,
  PRICING_STATUS,
  catalogStatusLabel,
  isCatalogStatusInReview,
} from '../../utils/alaCarteCatalog';
import {
  DELIVERABLE_ORDER_DOC_ID,
  readDeliverableOrderFromSettingsDocs,
  sortCatalogByDeliverableOrder,
} from '../../utils/deliverableOrder';
import { isAlaCarteItemHiddenFromPurchase } from '../../utils/programPricing';
import AlaCarteCatalogStructureEditor from '../../components/crm/AlaCarteCatalogStructureEditor';
import { getPortalEmoji } from '../../utils/portalEmojis';
import { useTheme } from '../../contexts/ThemeContext';
import PortalBackground from '../../components/PortalBackground';

// Lazy-loaded so the full emoji picker (~large bundle) only downloads when opened.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const EMPTY_ITEM = {
  name: '',
  description: '',
  department: 'General',
  pricingType: 'standard', // 'standard', 'child', 'caseByCase', or 'alwaysIncluded'
  parentItemId: '', // Used when pricingType is 'child'
  standardPricing: {
    cost: '',
    unit: '',
    stripeProductId: ''
  },
  hasDeliverable: false,
  deliverable: {
    quantity: '',
    maxQuantity: '',
    period: 'One Time',
    programs: [], // Legacy — programs are assigned on the Programs template page
    trackParticipants: false
  },
  icon: '📦',
  popularAddOn: false,
  minimumTermMonths: '',
  caseByCaseMultiplier: '', // Our markup on internal cost; case-by-case items only
};

// The program pages' deliverable grids show every non-child item except the
// on-site visit. Unpromoted items never render there, so they stay out of the
// order entirely — one gets its spot back (via the name fallback) if it's
// promoted again later.
const isOrderableDeliverable = (item) =>
  item.pricingType !== 'child' &&
  !/on[- ]?site\s*visit|onsite\s*visit/i.test(item?.name || '') &&
  !isAlaCarteItemHiddenFromPurchase(item);

// Card treatment by status: only published items render at full strength.
// Draft/unpromoted fade back; in-review fades back with a gold wash. Hovering
// any faded card brings it up to full so it stays readable.
const getCardStatusStyles = (pricingStatus) => {
  const status = pricingStatus || PRICING_STATUS.draft;
  if (status === PRICING_STATUS.published) {
    return {
      card: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
      pill: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    };
  }
  if (isCatalogStatusInReview(status)) {
    return {
      card: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/60 opacity-70 hover:opacity-100',
      pill: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
    };
  }
  // Draft + unpromoted
  return {
    card: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-55 hover:opacity-100',
    pill: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  };
};

const AlaCarteTemplate = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [parentSearch, setParentSearch] = useState('');

  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState(EMPTY_ITEM);

  // Deliverable order (settings/deliverableOrder) — drives the program pages' grids.
  const [deliverableOrderIds, setDeliverableOrderIds] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderDraft, setOrderDraft] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const departments = ['All', 'General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];

  // Load data from Firestore
  useEffect(() => {
    const loadData = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'alaCarteItems'));
        const itemsData = querySnapshot.docs.map((d) => normalizeAlaCarteItem(d.id, d.data()));
        setItems(itemsData);
        const settingsSnapshot = await getDocs(collection(db, 'settings'));
        setDeliverableOrderIds(readDeliverableOrderFromSettingsDocs(settingsSnapshot.docs));
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Open with the order the program pages currently render: saved order first,
  // then anything new ranked by the name fallback.
  const openOrderModal = () => {
    setOrderDraft(sortCatalogByDeliverableOrder(items.filter(isOrderableDeliverable), deliverableOrderIds));
    setShowOrderModal(true);
  };

  const moveOrderItem = (index, direction) => {
    setOrderDraft((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const saveDeliverableOrder = async () => {
    setSavingOrder(true);
    try {
      const order = orderDraft.map((item) => item.id);
      await setDoc(doc(db, 'settings', DELIVERABLE_ORDER_DOC_ID), {
        order,
        updatedAt: new Date().toISOString(),
      });
      setDeliverableOrderIds(order);
      setShowOrderModal(false);
    } catch (error) {
      console.error('Error saving deliverable order:', error);
      alert('Could not save the deliverable order.');
    } finally {
      setSavingOrder(false);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (item.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = selectedDepartment === 'All' || item.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  // Group filtered items by department so each department renders under its own
  // header (mirrors the portal Sales Resources page layout). Departments follow
  // the configured order; any unrecognized department falls to the end.
  const departmentOrder = departments.filter(dept => dept !== 'All');
  const groupedItems = filteredItems.reduce((groups, item) => {
    const dept = item.department || 'General';
    (groups[dept] = groups[dept] || []).push(item);
    return groups;
  }, {});
  // Within each department, items with a deliverable come first.
  Object.keys(groupedItems).forEach((dept) => {
    groupedItems[dept].sort((a, b) => (b.hasDeliverable ? 1 : 0) - (a.hasDeliverable ? 1 : 0));
  });
  const orderedDepartments = [
    ...departmentOrder.filter(dept => groupedItems[dept]?.length),
    ...Object.keys(groupedItems).filter(dept => !departmentOrder.includes(dept)),
  ];

  const handleAddItem = async () => {
    if (newItem.name && newItem.description) {
      try {
        let itemData;

        // Optional minimum term (months) — blank means none.
        const minimumTermMonths = String(newItem.minimumTermMonths ?? '').trim()
          ? Math.max(1, parseInt(String(newItem.minimumTermMonths).trim(), 10) || 1)
          : '';

        if (newItem.pricingType === 'child') {
          if (!newItem.parentItemId) {
            alert('Please select a parent item for this child item.');
            return;
          }
          itemData = {
            name: newItem.name,
            description: newItem.description,
            department: newItem.department,
            pricingType: 'child',
            parentItemId: newItem.parentItemId,
            hasDeliverable: newItem.hasDeliverable || false,
            deliverable: newItem.deliverable || { quantity: '', maxQuantity: '', period: 'One Time', programs: [] },
            icon: newItem.icon || '📦',
            createdAt: new Date()
          };
        } else if (newItem.pricingType === 'caseByCase') {
          const rawMultiplier = String(newItem.caseByCaseMultiplier ?? '').trim();
          let caseByCaseMultiplier = '';
          if (rawMultiplier) {
            caseByCaseMultiplier = parseFloat(rawMultiplier);
            if (!Number.isFinite(caseByCaseMultiplier) || caseByCaseMultiplier <= 0) {
              alert('Multiplier must be a positive number, or left blank.');
              return;
            }
          }
          itemData = {
            name: newItem.name,
            description: newItem.description,
            department: newItem.department,
            pricingType: 'caseByCase',
            hasDeliverable: newItem.hasDeliverable || false,
            deliverable: newItem.deliverable || { quantity: '', maxQuantity: '', period: 'One Time', programs: [] },
            icon: newItem.icon || '📦',
            minimumTermMonths,
            caseByCaseMultiplier,
            createdAt: new Date()
          };
        } else if (newItem.pricingType === 'alwaysIncluded') {
          const cost = newItem.standardPricing?.cost;
          if (cost === '' || cost === undefined || cost === null) {
            alert('Please enter the price per month for Always Included items.');
            return;
          }
          itemData = {
            name: newItem.name,
            description: newItem.description,
            department: newItem.department,
            pricingType: 'alwaysIncluded',
            standardPricing: {
              cost: parseFloat(newItem.standardPricing.cost),
              unit: 'month',
              stripeProductId: newItem.standardPricing?.stripeProductId || ''
            },
            hasDeliverable: newItem.hasDeliverable || false,
            deliverable: newItem.deliverable || { quantity: '', maxQuantity: '', period: 'One Time', programs: [] },
            icon: newItem.icon || '📦',
            createdAt: new Date()
          };
        } else {
          // Standard pricing
          if (!newItem.standardPricing?.cost || !newItem.standardPricing?.unit) {
            alert('Please enter both cost and unit for standard pricing.');
            return;
          }

          itemData = {
            ...newItem,
            pricingType: 'standard',
            standardPricing: {
              cost: parseFloat(newItem.standardPricing.cost),
              unit: newItem.standardPricing.unit,
              stripeProductId: newItem.standardPricing?.stripeProductId || ''
            },
            minimumTermMonths,
            createdAt: new Date()
          };
        }

        itemData = { ...itemData, popularAddOn: newItem.popularAddOn === true };

        const docRef = await addDoc(collection(db, 'alaCarteItems'), itemData);
        const newItemWithId = normalizeAlaCarteItem(docRef.id, itemData);

        setItems([...items, newItemWithId]);
        setNewItem(EMPTY_ITEM);
        setParentSearch('');
        setShowAddModal(false);
      } catch (error) {
        console.error('Error adding item:', error);
        alert('Error adding item. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 md:px-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/crm/templates" />
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center">
            À La Carte Solutions
          </h1>
          <button
            onClick={openOrderModal}
            title="Set the order deliverables appear in on the program pages"
            className="flex shrink-0 items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowUpDown className="h-4 w-4" />
            Deliverable Order
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            {/* Department Filter */}
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400 h-5 w-5" />
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            {/* Add New Button */}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add New Item
            </button>
          </div>
        </div>

        {/* Items grouped by department, each under its own centered header */}
        {orderedDepartments.map(dept => (
          <div key={dept} className="mt-10 first:mt-0">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
              <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                <span className="text-xl leading-none">{getPortalEmoji(dept)}</span>
              </span>
              <h2 className="text-xl md:text-2xl font-bold tracking-tight leading-tight text-center text-gray-900 dark:text-white">
                {dept}
              </h2>
              <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {groupedItems[dept].map(item => {
                const statusStyles = getCardStatusStyles(item.pricingStatus);
                return (
                <div
                  key={item.id}
                  className={`relative rounded-lg border shadow-sm transition-all hover:shadow-md cursor-pointer ${statusStyles.card}`}
                  onClick={() => navigate(`/crm/alacartetemplate/${item.id}`)}
                >
                  {/* Popular add-on star, top left */}
                  {item.popularAddOn === true && (
                    <Star
                      className="absolute top-2 left-2 h-5 w-5 fill-yellow-400 text-yellow-400"
                      aria-label="Popular add on"
                    />
                  )}

                  {/* Green deliverable badge, top right */}
                  {item.hasDeliverable && (
                    <span
                      className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-[11px] font-bold text-white"
                      title="Includes a deliverable"
                    >
                      1
                    </span>
                  )}

                  <div className="p-6 text-center">
                    <div className="text-4xl mb-4">{item.icon || '📦'}</div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {item.name}
                    </h3>
                    <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusStyles.pill}`}>
                      {catalogStatusLabel(item.pricingStatus)}
                    </div>
                    <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                      {item.pricingType === 'child'
                        ? `Part of ${items.find(p => p.id === item.parentItemId)?.name || 'parent'}`
                        : item.pricingType === 'caseByCase'
                          ? 'Case by Case'
                          : item.pricingType === 'alwaysIncluded'
                            ? `Always Included • $${(item.standardPricing?.cost || 0).toLocaleString()}/month`
                            : (item.pricingType === 'packages' || !item.pricingType)
                              ? `${item.packages?.length || 0} package${(item.packages?.length || 0) !== 1 ? 's' : ''} • ${(item.packages || []).reduce((total, pkg) => total + (pkg.items?.length || 0), 0)} items`
                              : `$${(item.standardPricing?.cost || 0).toLocaleString()} per ${item.standardPricing?.unit || 'item'}`
                      }
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8 text-center mt-6">
            <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <Package className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No items found</h3>
            <p className="text-gray-500 dark:text-gray-400">Try adjusting your search or filter, or add a new item.</p>
          </div>
        )}
      </div>

      {/* Deliverable order — drives the deliverable grids on the program pages */}
      <Modal isOpen={showOrderModal} onClose={() => setShowOrderModal(false)} zIndex={50}>
        <div className="p-6 max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1 text-center">Deliverable Order</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
            The order deliverables appear in on the program pages. Unpromoted items aren't listed — they don't show
            there at all.
          </p>

          {orderDraft.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No deliverables to order yet.</p>
          ) : (
            <div className="space-y-2">
              {orderDraft.map((item, index) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm px-3 py-2"
                >
                  <span className="w-6 shrink-0 text-center text-sm font-semibold text-gray-400 dark:text-gray-500">
                    {index + 1}
                  </span>
                  <span className="text-xl leading-none">{item.icon || '📦'}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
                    {item.name}
                  </span>
                  {item.pricingStatus !== PRICING_STATUS.published && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {catalogStatusLabel(item.pricingStatus)}
                    </span>
                  )}
                  <span className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveOrderItem(index, -1)}
                      disabled={index === 0}
                      title="Move up"
                      className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-30 disabled:hover:bg-transparent dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveOrderItem(index, 1)}
                      disabled={index === orderDraft.length - 1}
                      title="Move down"
                      className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-30 disabled:hover:bg-transparent dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowOrderModal(false)}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveDeliverableOrder}
              disabled={savingOrder || orderDraft.length === 0}
              className="px-6 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
            >
              {savingOrder ? 'Saving…' : 'Save Order'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add New Item Modal — header fields here, everything else via the shared editor */}
      <Modal isOpen={showAddModal} onClose={() => { setShowAddModal(false); setParentSearch(''); }} zIndex={50}>
        <div className="p-6 max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Add New Item</h2>

          <div className="space-y-4">
            {/* Icon Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Icon
              </label>
              <button
                onClick={() => setShowEmojiPicker(true)}
                className="w-16 h-16 rounded-full border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 flex items-center justify-center text-2xl hover:border-red-500 dark:hover:border-red-400 transition-colors"
              >
                {newItem.icon}
              </button>
            </div>

            {/* Item Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Item Name
              </label>
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter item name"
              />
            </div>

            {/* Department + Popular Add On */}
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={newItem.department}
                onChange={(e) => setNewItem({ ...newItem, department: e.target.value })}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                {departmentOrder.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-800/50">
                <input
                  type="checkbox"
                  checked={newItem.popularAddOn === true}
                  onChange={(e) => setNewItem({ ...newItem, popularAddOn: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Popular Add On</span>
              </label>
            </div>

            {/* Description, pricing, choice, deliverables, price */}
            <AlaCarteCatalogStructureEditor
              draft={newItem}
              setDraft={setNewItem}
              catalogItems={items}
              parentSearch={parentSearch}
              setParentSearch={setParentSearch}
            />
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => { setShowAddModal(false); setParentSearch(''); }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddItem}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Add Item
            </button>
          </div>
        </div>
      </Modal>

      {/* Icon Picker Modal */}
      <Modal isOpen={showEmojiPicker} onClose={() => setShowEmojiPicker(false)} maxWidth="xl" zIndex={100}>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
          Choose an icon
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 text-center">
          {newItem.name || 'New item'}
        </p>
        <div className="flex justify-center">
          {showEmojiPicker && (
            <React.Suspense
              fallback={
                <div className="flex justify-center items-center h-72 w-full">
                  <BBALoader size={32} />
                </div>
              }
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => {
                  setNewItem({ ...newItem, icon: emojiData.emoji });
                  setShowEmojiPicker(false);
                }}
                theme={darkMode ? 'dark' : 'light'}
                width="100%"
                height={380}
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </React.Suspense>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          {newItem.icon && newItem.icon !== '📦' ? (
            <button
              onClick={() => {
                setNewItem({ ...newItem, icon: '📦' });
                setShowEmojiPicker(false);
              }}
              className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              Use default icon
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={() => setShowEmojiPicker(false)}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AlaCarteTemplate;
